import React, { useState, useRef, useEffect, useCallback } from "react";
import "devextreme/dist/css/dx.light.css";
import {
    Column,
    Selection,
    FilterRow,
    Paging,
    ColumnChooser,
    Pager,
    SearchPanel,
    GroupPanel,
    Grouping,
    StateStoring,
} from "devextreme-react/data-grid";
import {
    HeaderFilter,
    FilterPanel,
    FilterBuilderPopup,
    Export
} from "devextreme-react/data-grid";
import BaseDataGrid from '../components/BaseDataGrid';
import { jsPDF } from "jspdf";
import { exportDataGrid as exportDataGridToPdf } from "devextreme/pdf_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { exportDataGrid as exportDataGridToExcel } from "devextreme/excel_exporter"; // Aliased for clarity
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import IconButton from "@mui/material/IconButton"; // Still used by renderPayment
import { Snackbar, Alert } from "@mui/material";
import httpRequest from "../HttpService";
import moment from "moment";
// Removed: import { useUserContext} from "../lib/user_context"; - sessionAccount is from props now.
// Assuming sessionAccount will be passed via props if needed, or useUserContext if preferred at this level.
// For now, using props.account as per original structure of similar components.

export default function InvoicesListDatagrid(props) {
  // props.account is used for fetching data, similar to original invoiceDatagrid.js
  const { account, setTotalInvoices, setSelectedInvoices } = props;

  const [invoices, setInvoices] = useState([]);
  const [openSnack, setOpenSnack] = useState(false);
  const [alertMsg, setAlertMsg] = useState('');
  const dataTable = useRef(null);

  const getInvoiceList = useCallback(async (currentAccount) => {
    if (!currentAccount) return; // Do not fetch if account is not available

    try {
      const body = await httpRequest.getInvoices(currentAccount);
      const invList = [];
      // Ensure httpRequest.getInvoices returns the downloadHTML field for each invoice
      // console.log("getInvoices return (sample):" + JSON.stringify(body.data[0]));

      body.data.forEach((value) => {
        invList.push({
          ...value, // Spread existing values
          id: invList.length, // Assign a unique id for the grid key
          dtGenerated: moment(value.dtGenerated).format("YYYY-MM-DD"),
          inv_date: moment(value.inv_date).format("YYYY-MM-DD"),
          selected: false,
          // downloadHTML should be part of 'value' from the API
        });
      });

      invList.sort(dynamicSort_reverse("dtGenerated"));
      setInvoices(invList);

      // Calculate total outstanding for invoices that have an amount owing
      const owingInvoices = invList.filter((value) => value.invOwing > 0);
      // Consider limiting to first 100 as in original, if that's a business rule:
      // const owingInvoices = invList.slice(0, 100).filter((value) => value.invOwing > 0);

      let totalAmount = owingInvoices.reduce((sum, curr) => sum + (curr.invOwing || 0), 0);
      let totalInvoiceNumbers = owingInvoices.map(value => value.invoiceNumber);

      // setTotalInvoices is a prop function from the parent component (Invoices.js)
      // Its stability is crucial (should be wrapped in useCallback in parent).
      if (setTotalInvoices) {
        setTotalInvoices(totalInvoiceNumbers, totalAmount.toFixed(2));
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setAlertMsg("Failed to fetch invoices: " + error.message);
      setOpenSnack(true);
      setInvoices([]); // Clear invoices on error to prevent stale data display
    }
  }, [setTotalInvoices]); // Dependency: setTotalInvoices prop

  useEffect(() => {
    // Fetch invoices when the account prop changes or getInvoiceList function reference changes.
    // The latter changes if its own dependencies (setTotalInvoices) change.
    getInvoiceList(account);
  }, [account, getInvoiceList]);

  // Renders the HTML content for download links.
  // The HTML is expected to be provided by the 'downloadHTML' field from the API.
  const renderDownloadHtmlColumn = (cellData) => {
    const htmlContent = cellData.value || ""; // Default to empty string if no HTML
    // dangerouslySetInnerHTML is used here because the HTML is from a trusted backend source.
    // Ensure the backend sanitizes this HTML if there's any remote chance of XSS.
    return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
  };

  // Renders a payment icon/button for eligible invoices.
  const renderPayment = (params) => {
    // Assuming 'account' prop determines if payment button is shown (e.g., for '9910')
    // Or use a more specific prop if 'account' is just for data fetching.
    const isPayableAccount = account === '9910'; // Example condition

    if (!isPayableAccount) {
        return <></>;
    }

    return (
      <IconButton
        disabled={params.data.invOwing === null || params.data.invOwing <= 0}
        onClick={() => {
          // Ensure 'account' used in submitPayment is the correct one for the transaction.
          submitPayment(params.data.invTotalDue, params.data.invoiceNumber, account);
        }}
        title="Pay Invoice"
      >
        <MonetizationOnIcon />
      </IconButton>
    );
  };

  // Submits a payment transaction.
  const submitPayment = async (amount, invoiceNumber, transactionAccount) => {
    const paymentAmount = Math.round(amount * 100) / 100;
    try {
        const response = await httpRequest.getTransactionToken(transactionAccount, paymentAmount, invoiceNumber.toString());
        // console.log("Transaction token response:", response.data);
        if (response.status !== 200 || !response.data || !response.data.token) {
            setAlertMsg("Failed to get transaction token. Please try again.");
            setOpenSnack(true);
        } else {
            const data = response.data;
            // TODO: Update this URL to the production ConvergePay URL when ready.
            window.open("https://api.demo.convergepay.com/hosted-payments?ssl_txn_auth_token=" + data.token, "_self");
        }
    } catch (error) {
        console.error("Transaction token error:", error);
        setAlertMsg("Transaction failed: " + (error.response?.data?.message || error.message));
        setOpenSnack(true);
    }
};

  // Handles row selection changes in the grid.
  const onSelectionChanged = ({ selectedRowsData }) => {
    // This function is called when row selection changes.
    // Update parent component's state for selected invoices if payment for multiple selected invoices is intended.
    // console.log("Selected rows data:", selectedRowsData);
    if (setSelectedInvoices) {
        let payment = 0.0;
        let selectedNumbers = [];
        selectedRowsData.forEach((value) => {
          // Assuming invTotalDue is the amount to pay for a selected invoice
          payment += (value.invTotalDue || 0);
          selectedNumbers.push(value.invoiceNumber);
        });
        setSelectedInvoices(selectedNumbers, payment.toFixed(2));
    }
  };

  // Sorts an array of objects by a property in reverse (descending) order.
  function dynamicSort_reverse(property) {
    return function (a, b) {
      if (a[property] < b[property]) return 1;
      if (a[property] > b[property]) return -1;
      return 0;
    };
  }

  // Handles grid data exporting.
  const onExporting = (e) => {
      const dataGrid = dataTable.current.instance(); // Get DataGrid instance
      if (e.format === "xlsx") {
            const workbook = new Workbook();
            const worksheet = workbook.addWorksheet("Invoices");
            exportDataGridToExcel({ // Use the aliased import
                component: dataGrid,
                worksheet,
                autoFilterEnabled: true,
            }).then(() => {
                 workbook.xlsx.writeBuffer().then((buffer) => {
                    saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Invoices.xlsx");
                });
            });
      } else if (e.format === "pdf") {
            const doc = new jsPDF();
            exportDataGridToPdf({
                jsPDFDocument: doc,
                component: dataGrid,
            }).then(() => {
                doc.save("Invoices.pdf");
            });
      } else if (e.format === "csv") { // Added CSV export consistency
            const workbook = new Workbook();
            const worksheet = workbook.addWorksheet("Invoices");
            exportDataGridToExcel({ // DevExtreme uses its excel_exporter for CSV as well
                component: dataGrid,
                worksheet: worksheet,
                format: 'csv' // Specify CSV format to the exporter
            }).then(() => {
                workbook.csv.writeBuffer().then((buffer) => {
                    saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Invoices.csv");
                });
            });
      }
      if (e.format !== 'xlsx' && e.format !== 'pdf' && e.format !== 'csv') {
        // Let the grid handle other formats if any, or just cancel for unsupported ones.
      } else {
        e.cancel = true; // Cancel default export for handled formats
      }
    };

    const exportFormats = ["pdf", "xlsx", "csv"];
    const filterBuilderPopupPosition = { of: window, at: "top", my: "top", offset: { y: 10 } };

  return (
    <div>
    <BaseDataGrid
      ref={dataTable}
      dataSource={invoices}
      showBorders={true}
      allowColumnReordering={true}
      allowColumnResizing={true}
      columnAutoWidth={true}
      columnResizingMode="widget"
      keyExpr="id"
      onExporting={onExporting}
      onRowPrepared={(e) => {
        if (e.rowType === 'data') {
            e.rowElement.style.backgroundColor = e.rowIndex % 2 === 0 ? "#FFFFFF" : "#E5E7E9";
        }
      }}
      onSelectionChanged={onSelectionChanged}
      hoverStateEnabled={true}
    >

    <FilterPanel visible={true} />
    <FilterBuilderPopup position={filterBuilderPopupPosition} />
    <HeaderFilter visible={true} />
    <GroupPanel visible={true} />
    <Grouping autoExpandAll={false} />
    <SearchPanel visible={true} width={240} placeholder="Search..." />

    {/* Configure selection mode based on requirements (e.g., paying selected) */}
    {/* If paying individual invoices via icon, multiple selection might be for other purposes or disabled */}
    <Selection mode="multiple" showCheckBoxesMode="onClick" />
    {/* <Selection mode="single" /> */}


    <FilterRow visible={true} />
    <Paging defaultPageSize={15} />
    <Pager
      visible={true}
      allowedPageSizes={[15, 30, 50, 100]} // Expanded options
      showPageSizeSelector={true}
      showInfo={true}
      // infoText="Page #{0}. Total: {1} ({2} items)" // Default infoText is usually sufficient
      showNavigationButtons={true} // Ensure navigation buttons are explicitly shown
      displayMode="full" // "full" or "compact"
    />
    <Column dataField="invoiceNumber" caption="Invoice Number" width={160} />
    <Column dataField="inv_date" caption="Invoice Date" width={140} alignment="center" dataType="date" format="yyyy-MM-dd" />
    <Column 
      dataField="invAmount" 
      caption="Invoice Amt" 
      width={130} 
      alignment="right" 
      dataType="number" 
      format={{
        type: "currency",
        precision: 2,
        currency: "USD"
      }}
    />
    <Column 
      dataField="invGST" 
      caption="GST" 
      width={100} 
      alignment="right" 
      dataType="number" 
      format={{
        type: "currency",
        precision: 2,
        currency: "USD"
      }}
    />
    <Column 
      dataField="invTotalDue" 
      caption="Total Due" 
      width={130} 
      alignment="right" 
      dataType="number" 
      format={{
        type: "currency",
        precision: 2,
        currency: "USD"
      }}
    />
    <Column 
      dataField="invOwing" 
      caption="Balance" 
      width={130} 
      alignment="right" 
      dataType="number" 
      format={{
        type: "currency",
        precision: 2,
        currency: "USD"
      }}
    />

    {/* Download column using the new downloadHTML field - NOW CENTERED */}
    <Column
        dataField="downloadHTML"
        caption="Downloads"
        width={250}
        alignment="center" // Changed from "left" to "center"
        cellRender={renderDownloadHtmlColumn}
        allowSorting={false}
        allowFiltering={false}
        allowExporting={false} // Raw HTML is not ideal for export
    />

    {/* Pay column: Renders a payment button/icon */}
    <Column
        caption="Pay" // No dataField needed if it's just an action button container
        width={100}
        alignment="center"
        cellRender={renderPayment}
        allowExporting={false}
    />
    <ColumnChooser enabled={true} mode="select" />

    <Export enabled={true} formats={exportFormats} allowExportSelectedData={true} />
    <StateStoring enabled={true} type="localStorage" storageKey="datagrid-invoices-list-state" />
  </BaseDataGrid>
   <Snackbar
        open={openSnack}
        autoHideDuration={6000} // Increased duration for error messages
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => {setOpenSnack(false)}}
      >
        <Alert
          onClose={() => {setOpenSnack(false)}}
          severity={alertMsg.startsWith("Failed") || alertMsg.startsWith("Transaction failed") ? "error" : "info"} // Dynamic severity
          sx={{ width: "100%" }}
        >
          {alertMsg}
        </Alert>
      </Snackbar>
  </div>
  );
}